// Wave F4 NAMEPLATE, widened by VOCAB-1. React access to the merchant
// vocabulary. Loads once per sub-account (cached in the lib) and exposes the
// resolved words, the status MEANINGS, and tiny helpers — so nav labels, page
// titles, headings, site-builder defaults and the portal never hardcode a core
// noun. Before the load resolves it returns the built-in defaults, so there is
// no empty flash and the render is byte-identical to today.
//
// TWO READS, ONE HOOK, ON PURPOSE. A word and what that word MEANS are
// different things stored in different places - vocabulary_settings.words for
// the label, job_status_meanings for the flags - and keeping them apart is the
// whole point of this wave. But a caller almost always wants both, so they
// load together here rather than making every screen mount two hooks.
//
// THE THREE STATES REACH THE CALLER. `warning` is non-null only when a read
// actually failed; `usingDefaults` says we asked and the business had set
// nothing. A screen that shows the merchant their own words can now tell them
// when it could not.

import { useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_VOCABULARY,
  jobStatusLabel as resolveJobStatusLabel,
  resolveVocabularyState,
  vocabWord,
  type Vocabulary,
  type VocabNoun,
  type VocabularyState,
  type WordOpts,
} from '../lib/vocabulary'
import { loadVocabularyState } from '../lib/vocabularySettings'
import {
  builtinLoadedJobStatusMeanings,
  loadJobStatusMeanings,
  meaningOf,
  resolveJobStatusMeanings,
  type JobStatusMeaning,
  type JobStatusMeaningsState,
  type LoadedJobStatusMeanings,
} from '../lib/jobStatusMeanings'
import { useOptionalAuth } from '../providers/AuthProvider'

export interface UseVocabulary {
  /** True only when the merchant's own configuration is on screen. */
  loaded: boolean
  /** True when we read successfully and the business had set nothing. */
  usingDefaults: boolean
  /** Non-null ONLY when a read failed. Never set for an empty configuration. */
  warning: string | null
  vocab: Vocabulary
  /** The merchant's word for a noun (plural/capital aware). */
  word: (noun: VocabNoun, opts?: WordOpts) => string
  /** The merchant's label for a job status code (code stays semantic-fixed). */
  jobStatusLabel: (status: string) => string
  /** WHAT A STATUS MEANS. Ask this, never the word. */
  meaning: (status: string | null | undefined) => JobStatusMeaning
  statusMeanings: LoadedJobStatusMeanings
}

export function useVocabulary(): UseVocabulary {
  // OPTIONAL, deliberately. A dashboard widget or a settings card is often
  // rendered on its own - in a test, or inside a shell that has not mounted
  // the provider yet - and a hard useAuth() makes every such render throw.
  // PLATFORM-1 added this seam for the same reason. No account means no
  // business to ask, which resolves to our words: byte-identical to today.
  const currentSubAccountId = useOptionalAuth()?.currentSubAccountId ?? null
  const [state, setState] = useState<VocabularyState>({ state: 'loading' })
  const [meanings, setMeanings] = useState<JobStatusMeaningsState>({ state: 'loading' })

  useEffect(() => {
    let cancelled = false
    setState({ state: 'loading' })
    setMeanings({ state: 'loading' })
    void loadVocabularyState(currentSubAccountId).then((s) => {
      if (!cancelled) setState(s)
    })
    void loadJobStatusMeanings(currentSubAccountId).then((m) => {
      if (!cancelled) setMeanings(m)
    })
    return () => {
      cancelled = true
    }
  }, [currentSubAccountId])

  return useMemo(() => {
    const resolved = resolveVocabularyState(state)
    const v = resolved.vocab ?? DEFAULT_VOCABULARY
    const m = resolveJobStatusMeanings(meanings)
    // ONE warning line, whichever read failed. Two separate warnings for what
    // a merchant experiences as one thing ("my words did not load") would be
    // noise, and the reason string names which.
    const warning = resolved.warning ?? m.warning
    return {
      loaded: resolved.loaded && meanings.state === 'loaded',
      usingDefaults: resolved.usingDefaults,
      warning,
      vocab: v,
      word: (noun: VocabNoun, opts?: WordOpts) => vocabWord(v, noun, opts),
      jobStatusLabel: (status: string) => resolveJobStatusLabel(v, status),
      meaning: (status: string | null | undefined) => meaningOf(m.loaded, status),
      statusMeanings: m.loaded ?? builtinLoadedJobStatusMeanings(),
    }
  }, [state, meanings])
}
